'use client';

import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import type { ReactElement, ReactNode } from 'react';
import type { Key } from 'react-aria-components';
import { ToggleButtonGroup } from 'react-aria-components';

import type { IconProps } from '../Icon/Icon';
import { useFusionTheme } from '../hooks';

import SelectableChip from './SelectableChip/SelectableChip';
import styles from './SelectableChipGroup.module.scss';

const variants = cva('', {
  variants: {
    width: {
      full: styles.fullWidth,
      hug: ''
    }
  }
});

export interface Props<T extends Key = Key> extends VariantProps<typeof variants> {
  'aria-label'?: string;
  className?: string;
  isDisabled?: boolean;
  children?: ReactElement<typeof SelectableChip>[];
  labels?: [string, string];
  items?: {
    id: T;
    content: ReactNode;
    icon?: IconProps['icon'];
  }[];
  value?: T[];
  defaultValue?: T[];
  disallowEmptySelection?: boolean;
  onChange?: (selectedKeys: T[]) => void;
}

const SelectableChipGroup = <T extends Key = Key>({
  className,
  children,
  labels,
  items = [],
  value,
  defaultValue,
  disallowEmptySelection = true,
  width = 'hug',
  onChange,
  ...props
}: Props<T>) => {
  const { addThemeClassName } = useFusionTheme();
  const content = children
    ? children
    : items.map(({ content, ...item }) => (
        <SelectableChip<T> {...item} key={item.id}>
          {content}
        </SelectableChip>
      ));

  const onSelectionChange = (keys: Set<Key>) => {
    onChange?.([...keys] as T[]);
  };

  return (
    <div className={addThemeClassName(className, variants({ width }))}>
      {labels && (
        <div className={styles.labels}>
          {labels.map((label) => (
            <div className={styles.label} key={label}>
              {label}
            </div>
          ))}
        </div>
      )}
      <ToggleButtonGroup
        className={styles.group}
        selectionMode="single"
        disallowEmptySelection={disallowEmptySelection}
        selectedKeys={value}
        defaultSelectedKeys={defaultValue}
        onSelectionChange={onSelectionChange}
        {...props}
      >
        {content}
      </ToggleButtonGroup>
    </div>
  );
};

export default SelectableChipGroup;
